import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { CommonApiService } from "./common-api.service";
import { APIURL } from "../global/constant";
import { SharedService } from "./shared.service";
import { finalize, distinctUntilChanged, debounceTime } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class CreditService {
  private creditCount = new BehaviorSubject<number>(0);
  public $creditCount: Observable<number> = this.creditCount.asObservable();

  private isLoading = new BehaviorSubject<boolean>(false);
  public $isLoading: Observable<boolean> = this.isLoading.asObservable();

  private isRefreshing = false; // Flag to prevent duplicate simultaneous calls

  constructor(
    private commonApiService: CommonApiService,
    private sharedService: SharedService
  ) {
    // Subscribe to company changes to refresh credits
    // Use distinctUntilChanged to prevent duplicate calls for the same companyId
    // Use debounceTime to prevent rapid successive calls
    this.sharedService.$companyId
      .pipe(
        distinctUntilChanged(),
        debounceTime(100)
      )
      .subscribe((companyId) => {
        if (companyId && companyId > 0) {
          this.refreshCredits();
        }
      });
  }

  /**
   * Get current credit count value
   */
  getCreditCount(): number {
    return this.creditCount.getValue();
  }

  /**
   * Refresh credit count from API
   * Prevents duplicate simultaneous calls
   */
  refreshCredits(): void {
    // Prevent duplicate calls if already refreshing
    if (this.isRefreshing) {
      return;
    }

    const companyId = this.sharedService.getCompanyId();
    if (!companyId || companyId <= 0) {
      return;
    }

    this.isRefreshing = true;
    this.isLoading.next(true);
    this.commonApiService
      .get(APIURL.creditBalance)
      .pipe(finalize(() => {
        this.isLoading.next(false);
        this.isRefreshing = false;
      }))
      .subscribe(
        (res: any) => {
          if (res && res.statusCode === 200) {
            // Extract credit count from response
            // Adjust the property name based on your API response structure
            const creditCount =
              res.data?.remaining_balance ||
              res.data?.credits_balance ||
              res.data?.balance ||
              res.data?.credits ||
              0;
            this.creditCount.next(creditCount);
          }
        },
        (error: any) => {
          console.error("Error fetching credit balance:", error);
          // Optionally show error notification
          // this.sharedService.errorToastNotification("Failed to fetch credit balance");
        }
      );
  }

  /**
   * Update credit count manually (useful after credit consumption)
   */
  updateCreditCount(count: number): void {
    this.creditCount.next(count);
  }

  /**
   * Decrement credit count (useful for optimistic updates)
   */
  decrementCredits(amount: number): void {
    const currentCount = this.creditCount.getValue();
    this.creditCount.next(Math.max(0, currentCount - amount));
  }
}

